import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

import WebsitePageWrapper from './WebsitePageWrapper';
import { useNotification } from '../../../context/NotificationContext';

const WebsiteThemePage = ({ userOrganizationId }) => {
  return (
    <WebsitePageWrapper
      title="Theme & Branding"
      subtitle="Customize your website's appearance"
      userOrganizationId={userOrganizationId}
    >
      {ThemeContent}
    </WebsitePageWrapper>
  );
};

const ThemeContent = ({ website, updateWebsite, isSaving }) => {
  const { getAccessTokenSilently } = useAuth0();
  const notification = useNotification();
  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  const [primaryColor, setPrimaryColor] = useState(website?.theme?.primary_color || '#ff8264');
  const [logoUrl, setLogoUrl] = useState(website?.theme?.logo_url || '');
  const [bannerUrl, setBannerUrl] = useState(website?.theme?.banner_image_url || '');
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    setPrimaryColor(website?.theme?.primary_color || '#ff8264');
    setLogoUrl(website?.theme?.logo_url || '');
    setBannerUrl(website?.theme?.banner_image_url || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [website?._id]);

  const handleSave = async () => {
    await updateWebsite({
      theme: {
        primary_color: primaryColor,
        logo_url: logoUrl,
        banner_image_url: bannerUrl,
      },
    });
  };

  const handleImageUpload = async (file, type) => {
    if (!file) return;

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      notification.error('Image must be less than 5MB');
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      notification.error('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    try {
      setIsUploading(true);
      const token = await getAccessTokenSilently();
      const fileName = `${type}-${Date.now()}-${file.name}`;
      const signedUrlResponse = await fetch(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/s3/signed-url?type=store-asset&fileName=${encodeURIComponent(fileName)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!signedUrlResponse.ok) throw new Error('Failed to get upload URL');

      const { signedUrl, fileUrl } = await signedUrlResponse.json();

      await fetch(signedUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });

      if (type === 'logo') {
        setLogoUrl(fileUrl);
      } else {
        setBannerUrl(fileUrl);
      }

      notification.success('Image uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      notification.error('Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <div className="website-page-content">
        <div className="website-card">
          <div className="website-card-header">
            <h2 className="website-card-title">Brand Colors</h2>
            <p className="website-card-subtitle">
              Set your primary brand color for buttons, links, and accents
            </p>
          </div>

          <div className="website-card-body">
            {/* Primary Color */}
            <div className="website-form-group">
              <label className="website-form-label">Primary Color:</label>
              <div className="website-color-picker">
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="website-color-input"
                />
                <input
                  type="text"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  placeholder="#ff8264"
                  className="website-form-input website-color-text"
                />
              </div>
            </div>

            {/* Store Logo */}
            <div className="website-form-section">
              <h3 className="website-form-section-title">Store Logo</h3>
              <p className="website-form-hint">
                Upload your store logo. Recommended size: 200x200 pixels, PNG or SVG format.
              </p>

              {!logoUrl ? (
                <label className="website-upload-area square">
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e.target.files[0], 'logo')}
                    className="website-upload-input"
                    disabled={isUploading}
                  />
                  <div className="website-upload-content">
                    <UploadIcon className="website-upload-icon" />
                    <span className="website-upload-text">
                      {isUploading ? 'Uploading...' : 'Upload Logo'}
                    </span>
                    <span className="website-upload-hint">No logo</span>
                  </div>
                </label>
              ) : (
                <div className="website-image-preview-container">
                  <div className="website-image-preview logo">
                    <img src={logoUrl} alt="Store logo" />
                  </div>
                  <div className="website-image-preview-footer">
                    <span>Logo uploaded</span>
                    <button
                      onClick={() => setLogoUrl('')}
                      className="website-remove-btn"
                    >
                      <DeleteIcon style={{ fontSize: 16 }} />
                      Remove
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Banner Image */}
            <div className="website-form-section">
              <h3 className="website-form-section-title">Banner Image</h3>
              <p className="website-form-hint">
                Upload a banner image for your website header. Recommended size: 1200x400 pixels.
              </p>

              {!bannerUrl ? (
                <label className="website-upload-area wide">
                  <input
                    ref={bannerInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e.target.files[0], 'banner')}
                    className="website-upload-input"
                    disabled={isUploading}
                  />
                  <div className="website-upload-content">
                    <UploadIcon className="website-upload-icon" />
                    <span className="website-upload-text">
                      {isUploading ? 'Uploading...' : 'Upload Banner'}
                    </span>
                    <span className="website-upload-hint">No banner</span>
                  </div>
                </label>
              ) : (
                <div className="website-image-preview-container">
                  <div className="website-image-preview banner">
                    <img src={bannerUrl} alt="Banner" />
                  </div>
                  <div className="website-image-preview-footer">
                    <span>Banner uploaded</span>
                    <button
                      onClick={() => setBannerUrl('')}
                      className="website-remove-btn"
                    >
                      <DeleteIcon style={{ fontSize: 16 }} />
                      Remove
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Action Bar */}
      <div className="website-action-bar">
        <button className="action-btn primary" onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Theme Settings'}
        </button>
      </div>
    </>
  );
};

export default WebsiteThemePage;
